export const en = {
  // Common
  common: {
    loading: 'Loading...',
    saving: 'Saving...',
    back: 'Back',
    show: 'Show',
    hide: 'Hide',
    day: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
    },
    dayAbbr: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
    month: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
    },
    relativeDates: {
      today: 'Today',
      tomorrow: 'Tomorrow',
      onDay: 'On {{day}}',
      inDays: 'In {{count}} days',
      inWeeks: 'In {{count}} weeks',
      days1: 'Day',
      days2_4: 'Days',
      days5plus: 'Days',
      weeks1: 'Week',
      weeks2_4: 'Weeks',
      weeks5plus: 'Weeks',
    },
    errors: {
      networkError: 'Network error. Please check your connection and try again.',
      serverError: 'Server error. Please try again later.',
      unexpected: 'Something went wrong. Please try again later.',
    },
  },

    // Terminology
    terms: {
      // Provider role
      provider: 'Coach',
      providers: 'Coaches',
      providerPossessive: 'Coach\'s',
      myProvider: 'My coach',
      myProviders: 'My coaches',
      
      // Receiver role
      receiver: 'Trainee',
      receivers: 'Trainees',
      receiverPossessive: 'Trainee\'s',
      myReceiver: 'My trainee',
      myReceivers: 'My trainees',
      
      // Session terminology
      session: 'Training',
      sessions: 'Trainings',
      sessionSet: 'Training set',
      sessionSets: 'Training sets',
      sessionTemplate: 'Training template',
      sessionTemplates: 'Training templates',
      
      // Actions
      createSession: 'Create training',
      editSession: 'Edit training',
      viewSession: 'View training',
      completeSession: 'Complete training',
      scheduleSession: 'Schedule training',
    },

  // User Relation Statuses
  userRelationStatus: {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
    PENDING_APPROVAL_BY_TRAINEE: 'Pending trainee approval',
    PENDING_APPROVAL_BY_COACH: 'Pending coach approval',
    DECLINED_BY_TRAINEE: 'Declined by trainee',
    DECLINED_BY_COACH: 'Declined by coach',
  },

  // Training Set Statuses
  trainingSetStatus: {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
  },

  // Training Projection Statuses
  trainingStatus: {
    SCHEDULED: 'Scheduled',
    COMPLETED: 'Completed',
    MOVED: 'Moved',
    SKIPPED: 'Skipped',
    CANCELLED: 'Cancelled',
    ACTIVE: 'Active',
    UPCOMING: 'Upcoming',
  },

  // Coach Dashboard
  dashboard: {
    welcome: 'Welcome, {{name}}!',
    subtitle: 'Your events dashboard',
    usernameWarning: {
      title: 'Add a Telegram username',
      message: 'Coaches need your @username to send invites. Set it once in Telegram Settings.',
      details: 'Open Telegram → Settings → Username, then return here.',
      cta: 'Open Telegram settings',
      showMore: 'Show more',
      showLess: 'Show less',
    },
    overLimitWarning: {
      title: 'Subscription limit exceeded',
      upgrade: 'Unlock',
      traineesExceeded: 'You\'ve reached your trainee limit',
    },
    quickActions: {
      title: 'Quick actions',
      generateReports: 'Generate reports',
      generateReportsDesc: 'View training analytics and progress',
      inviteTrainee: 'Invite trainee',
      inviteTraineeDesc: 'Send invitation to registered user',
      createVirtualTrainee: 'Create virtual trainee',
      createVirtualTraineeDesc: 'Add trainee without telegram',
      viewAllTrainees: 'View all trainees',
      viewAllTraineesDesc: 'Manage your trainees',
    },
    recentTrainees: {
      joined: 'Joined',
      title: 'Recent trainees',
      viewAll: 'View all',
    },
    emptyState: {
      title: 'No trainees yet',
      description: 'Start by inviting a trainee or creating a virtual trainee',
      action: 'Add your first trainee',
    },
    thisWeekTrainings: 'This week events',
    todayTrainings: 'Events today',
    nextTraining: 'Next event',
    myCoaches: {
      title: 'My specialists',
      description: 'View specialists you work with',
    },
    greetings: {
      morning: 'Good morning',
      afternoon: 'Good afternoon',
      evening: 'Good evening',
    },
    hero: {
      todayOverview: "Today's overview",
      sessionsSuffix: 'sessions scheduled',
      thisWeekChip: '{{count}} this week',
      activeTraineesChip: '{{count}} active trainees',
    },
    completableAlert: {
      title_one: '{{count}} set ready to complete',
      title_other: '{{count}} sets ready to complete',
      subtitle: 'Tap to review and finalize',
    },
    traineesSection: {
      title: 'My trainees',
      seeAll: 'See all',
      invite: 'Invite',
    },
    stats: {
      activeTrainees: 'Active trainees',
      completedSessions: 'Completed trainings',
      totalSessions: 'Total trainings',
    },
    assistantBanner: {
      title: 'Have questions about the app?',
      subtitle: 'Ask our AI assistant — it knows everything about Training Tracker.',
      cta: 'Ask Assistant',
    },
  },

  // Trainees List
  trainees: {
    virtualBadge: 'Virtual',
    filters: {
      all: 'All',
      active: 'Active',
      pendingByCoach: 'Pending approval',
      pendingByTrainee: 'Pending approval',
      declined: 'Declined',
    },
    sessions: 'Sessions',
    joined: 'Joined',
    emptySearch: {
      tryDifferent: 'Try a different search term',
      title: 'No trainees found',
      startInviting: 'Start by inviting trainees!',
    },
    title: 'My trainees',
    subtitle: 'Manage your trainees and their training sets',
    addButton: 'Add trainee',
    inviteButton: 'Invite trainee',
    searchPlaceholder: 'Search trainees...',
    total: '{{count}} total trainees',
  },

  // Contacts
  contacts: {
    title: 'Contacts',
    total: '{{total}} total ({{coaches}} coaches, {{clients}} clients)',
    searchPlaceholder: 'Search contacts...',
    filters: {
      all: 'All',
      coaches: 'Coaches',
      clients: 'Clients',
    },
    label: {
      coach: 'Coach',
      client: 'Client',
    },
    emptySearch: {
      title: 'No contacts found',
      tryDifferent: 'Try a different search query',
      noContacts: 'You don\'t have any contacts yet',
    },
  },

  // Invite Trainee
  invite: {
    title: 'Invite new client',
    subtitle: 'Send an invitation to a registered client',
    info: {
      title: 'How to invite',
      step1: '1. Ask the client for their Telegram username',
      step2: '2. Make sure the client is registered in this bot',
      step3: '3. Enter their username below (without @)',
      step4: '4. The client will receive an invite notification and can accept or decline',
      step5: '5. You will be notified via Telegram bot when they respond',
    },
    form: {
      username: 'Telegram username',
      usernameRequired: 'Telegram username *',
      usernamePlaceholder: 'Username',
      usernameHelp: 'Enter username without @ symbol',
      noTelegram: 'Client is not using Telegram?',
    },
    button: {
      submit: 'Send invite',
      submitting: 'Sending...',
      success: 'Invite sent!',
    },
    errors: {
      userNotFound: 'User with username "{{username}}" not found. Make sure they are registered in the bot.',
      alreadyInvited: 'This user has already been invited.',
      selfInvite: '😅 nice try—but you can\'t invite yourself.',
      unknown: 'Failed to send invite. Please try again.',
    },
    limitReached: {
      title: 'You can’t add more clients',
      body: 'Your current plan has reached its client limit. Unlock Pro to invite more.',
    },
  },

  // Create Virtual Trainee
  virtualTrainee: {
    title: 'Create virtual trainee',
    subtitle: 'Add a trainee without telegram account',
    info: {
      title: 'About virtual trainees',
      description: 'Virtual trainees are for tracking people who don\'t use Telegram. They won\'t receive notifications, but you can manage their training sets normally.',
    },
    form: {
      firstNameRequired: 'First name *',
      firstNamePlaceholder: 'John',
      lastName: 'Last name (optional)',
      lastNamePlaceholder: 'Doe',
      lastNameHelp: 'Last name is optional',
    },
    button: {
      submit: 'Create virtual trainee',
      submitting: 'Creating...',
      success: 'Virtual trainee created!',
    },
    errors: {
      invalidData: 'Please provide a valid first name (2-255 characters)',
      unknown: 'Failed to create virtual trainee. Please try again.',
    },
  },

  // Reports
  reports: {
    title: 'Reports',
    subtitle: 'Analytics and progress',
    comingSoon: {
      title: 'Coming soon',
      description: 'Analytics and progress reports',
    },
  },

  // All Training Sets
  allTrainingSets: {
    title: 'All event sets',
    subtitle: 'View all your event sets',
    canComplete: 'Can complete',
    unpaid: 'Unpaid',
    filters: {
      all: 'All',
      canBeCompleted: 'Ready',
      unpaid: 'Unpaid',
    },
    emptyState: {
      title: 'No training sets',
      description: 'You don\'t have any event sets yet',
    },
    error: {
      title: 'Error',
      tryAgain: 'Try again',
    },
  },

  activeTrainingSet: {
    waitingTitle: 'Not assigned yet',
    waitingBody: 'Your coach will assign a training set soon.',
  },

  // Complete Set Modal
  modal: {
    complete: {
      title: 'Complete training set',
      description: 'Are you sure you want to complete this training set?',
      autoProlongation: {
        label: 'Auto-prolongation',
        description: 'Create a new set starting from the day after this set ends',
      },
      cancel: 'Cancel',
      confirm: 'Complete',
    },
    pause: {
      title: 'Pause training set',
      startDate: 'Pause start date',
      endDate: 'Pause end date',
      cancel: 'Cancel',
      confirm: 'Pause',
      errors: {
        startDateInPast: 'Start date must be in the future',
        endDateInPast: 'End date must be in the future',
        endDateBeforeStart: 'End date must be after start date',
      },
    },
  },

  // Edit Training
  editTraining: {
    title: 'Edit training',
    form: {
      name: 'Start time',
      status: 'Status',
      statusOptions: {
        scheduled: 'Scheduled',
        active: 'Active',
        completed: 'Completed',
        moved: 'Moved',
        skipped: 'Skipped',
        cancelled: 'Cancelled',
      },
      date: 'Date',
      startTime: 'Start time',
      duration: 'Duration',
      hours: 'Hours',
      minutes: 'Minutes',
      notes: 'Comment / update reason',
      notesPlaceholder: 'Add a comment or reason for this update...',
      notesVisibleToTrainee: 'This comment will be visible to the trainee',
      markAsFired: 'Mark as fired 🔥',
      firedDescription: 'Fired trainings are highlighted with an orange background',
    },
    button: {
      save: 'Save changes',
      saving: 'Saving...',
      cancel: 'Cancel',
    },
    errors: {
      loadFailed: 'Failed to load training',
      updateFailed: 'Failed to update training',
    },
    success: {
      updated: 'Training updated successfully!',
    },
    skipLimit: {
      willBeSkipped: '{{planned}}/{{max}} trainings will be skipped',
      countOnly: 'Skipped trainings: {{current}}',
    },
    notificationInfo: 'The trainee will receive a notification about this edit with all the details',
  },

  // My Coaches
  myCoaches: {
    error: {
      title: 'Error',
      tryAgain: 'Try again',
    },
    emptyState: {
      title: 'No coaches yet',
      description: 'You don\'t have any coaches yet',
    },
    card: {
      trainingSets: 'Training sets',
    },
  },

  // User Detail
  userDetail: {
    backToTrainees: 'Back to clients',
    backToCoaches: 'Back to my specialists',
    openChat: 'Open chat',
    openChatNoUsername: 'This user has no Telegram username set.',
    virtualBadge: 'Virtual',
    error: {
      title: 'Error',
      back: 'Back to {{type}}',
    },
    edit: {
      button: 'Edit name',
      title: 'Edit virtual trainee',
      save: 'Save changes',
      saving: 'Saving...',
      cancel: 'Cancel',
    },
    delete: {
      buttonSpecialist: 'Delete specialist',
      buttonClient: 'Delete client',
      deleting: 'Deleting...',
      confirmTitle: '⚠️ important warning ⚠️',
      confirmMessage: 'Are you sure you want to delete this {{type}}?',
      confirmDetails: '🗑️ this will permanently delete:\n• the relationship\n• the assigned training program (if any)\n• all event sets and events\n• all related data\n\n❌ this action cannot be undone and all data will be lost forever!',
      errorTitle: '❌ failed to delete {{type}}:',
      errorRetry: 'Please try again.',
      typeSpecialist: 'specialist',
      typeClient: 'client',
    },
    invite: {
      accept: 'Accept',
      decline: 'Decline',
      accepting: 'Accepting...',
      declining: 'Declining...',
      acceptConfirm: 'Accept this invitation?',
      soloCancelWarning: 'Joining a coach will cancel your Solo subscription. Your features will depend on your coach\'s plan.',
      declineConfirm: 'Decline this invitation?',
      acceptError: '❌ failed to accept invitation',
      declineError: '❌ failed to decline invitation',
    },
    openHistory: 'History',
    openHistoryHint: 'Sets, programs, and workouts',
    programs: {
      title: 'Training Program',
      assignButton: 'Assign program',
      changeButton: 'Change program',
      noAssignment: 'No program assigned yet',
      ongoing: 'Ongoing',
      historyTitle: 'Assignment history',
      noHistory: 'No previous assignments',
      reassignButton: 'Assign again',
      saveToLibrary: 'Add to saved',
      unsavedRetentionHint: 'Unsaved programs older than 3 months are removed from history. Add them to saved to keep them.',
      durationLabel: 'Duration',
      periodLabel: 'Period',
      unsavedBadge: 'Unsaved',
      deassignAria: 'Remove program assignment',
    },
    trainingSets: {
      title: 'Event sets',
      noSets: 'No event sets yet',
      hasActiveSet: 'This client already has an active event set. Please complete or cancel the existing set before creating a new one.',
      createNew: 'Create new',
      cannotCreate: {
        title: 'Cannot create training set',
        description: 'Training sets can only be created for trainees with active status.',
      },
      trainingDays: 'Training days',
      plannedTrainings: 'Planned trainings:',
      status: 'Status',
      startDate: 'Start date',
      endDate: 'End date',
      sessionDuration: '/session',
      sessionsLabel: 'Sessions',
      scheduleLabel: 'Schedule',
      paused: 'Paused',
      deleteAria: 'Delete training set',
    },
    history: {
      title: 'Event history',
      noHistory: 'No completed event sets yet',
      retentionHint: 'Completed event sets older than 3 months are removed from history.',
    },
  },

  // Training Set Stats
  // Create Training Set
  createTrainingSet: {
    title: 'Create training set',
    subtitle: 'For {{trainee}}',
    useTemplate: 'Use template',
    tabs: {
      trainings: 'Trainings',
    },
    form: {
      name: 'Training set name',
      trainingDays: 'Training days',
      trainingDaysRequired: 'Training days *',
      plannedTrainings: 'Planned trainings',
      addTraining: 'Add training',
      trainingName: 'Training name',
      removeTraining: 'Remove',
      maxSkipCountHint: 'Maximum skipped trainings (0-365, cannot exceed planned trainings)',
      defaultStartTime: 'Default start time',
      defaultStartTimeHelp: 'Default time for all training days',
      hours: 'Hours',
      minutes: 'Minutes',
      sessionDuration: 'Session duration',
      startDate: 'Start date',
      maxSkipCount: 'Max skip count',
      maxSkipCountPlaceholder: 'E.g., 3',
    },
    errors: {
      previewFailed: 'Failed to load preview',
      startDateRequired: 'Start date is required',
      nameRequired: 'Training set name is required',
      noTrainings: 'At least one training is required',
      trainingNameRequired: 'Training name is required',
      createFailed: 'Failed to create training set',
      noDaysSelected: 'Please select at least one day for trainings',
      hasActiveSet: 'This trainee already has an active training set. Please complete or cancel the existing set before creating a new one.',
      traineeLimit: 'You have exceeded the trainee limit. Please upgrade your plan or remove some trainees to create training sets.',
      durationLimit: 'The training set duration is {current} days, but your plan allows a maximum of {max} days. Please reduce the number of trainings or upgrade your plan.',
    },
    button: {
      backToEdit: 'Back to edit',
      cannotCreate: 'Cannot create',
      creating: 'Creating...',
      create: 'Create training set',
      preview: 'Preview schedule',
    },
    messages: {
      createSuccess: 'Event set created successfully!',
    },
    success: {
      createSuccess: 'Training set created successfully!',
    },
    preview: {
      title: 'Training schedule preview',
      subtitle: '{{count}} sessions planned',
      durationExceeded: 'Duration limit exceeded',
      first: 'First',
      last: 'Last',
      loadingPreview: 'Loading preview...',
      overlapWarning: {
        description: 'This event set overlaps with an existing active event set. Please adjust the dates or complete/cancel the existing set before creating a new one.',
        title: 'Overlap warning',
      },
      session: 'Session',
      week: 'Week',
    },
    modal: {
      overlap: {
        description: 'This training set overlaps with an existing active training set for this trainee. Please adjust the dates or complete/cancel the existing set before creating a new one.',
      },
    },
    templateCard: {
      hours: 'Hours',
      minutes: 'Minutes',
      trainings: 'Trainings',
    },
  },

  // Training Set Detail (generic parts only - progress, actions, messages, errors, schedule)
  trainingSetDetail: {
    title: 'Event set details',
    subtitle: '{{count}} training sessions',
    loading: 'Loading trainings...',
    relatedUser: {
      coachLabel: 'Coach',
      traineeLabel: 'Trainee',
    },
    progress: {
      title: 'Progress',
      planned: 'Planned',
      completed: 'Completed',
      remaining: 'Remaining',
      fired: 'Fired',
      outOf: 'Out of',
      skippedCancelled: 'Skipped/cancelled',
      notCounted: 'Not counted',
    },
    actions: {
      getReport: 'Get report',
      sendingReport: 'Sending report...',
    },
    messages: {
      reportSent: 'Report sent successfully!',
      deleteSuccess: 'Events set deleted successfully!',
      completeSuccess: 'Events set completed successfully!',
      completeWithProlongation: 'Events set completed successfully! A new set has been created starting from the day after this set ends.',
      pauseSuccess: 'Training set paused successfully!',
      resumeSuccess: 'Training set resumed successfully!',
      cancelPauseSuccess: 'Pause canceled successfully! Projections have been recreated from the pause start date.',
      markUnpaidSuccess: 'Marked as unpaid',
      clearUnpaidSuccess: 'Marked as paid',
    },
    errors: {
      reportFailed: 'Failed to send report',
      pauseFailed: 'Failed to pause training set',
      resumeFailed: 'Failed to resume training set',
      cancelPauseFailed: 'Failed to cancel pause',
      deleteFailed: 'Failed to delete training set',
      completeFailed: 'Failed to complete training set',
      unpaidFailed: 'Failed to update unpaid mark',
    },
    confirmations: {
      resume: 'Are you sure you want to resume this training set? Projections will be regenerated from the original pattern.',
      cancelPause: 'Are you sure you want to cancel the pause? All projections generated during pause will be deleted and recreated from the pause start date.',
      delete: 'Are you sure you want to delete this training set? This action cannot be undone.',
      complete: 'Complete training set?',
    },
    pause: {
      title: 'This training set has a pause period',
      period: 'Pause period',
    },
    schedule: {
      title: 'Training schedule',
      week: 'Week',
      comment: 'Comment',
      first: 'First',
      last: 'Last',
      next: 'Next',
      session: 'Event',
      pastTrainings: 'Past events',
      training: 'Event',
      trainings: 'Events',
      emptyWeek: 'No trainings this week',
    },
    stats: {
      nextTraining: 'Next training',
    },
    emptyState: {
      title: 'No training set found',
    },
    empty: {
      title: 'No trainings found for this training set',
      description: 'This training set has no scheduled trainings',
    },
  },

  // Training Set Actions (generic button labels)
  trainingSetActions: {
    complete: 'Complete',
    completing: 'Completing...',
    delete: 'Delete',
    deleting: 'Deleting...',
    pause: 'Pause',
    pausing: 'Pausing...',
    resume: 'Resume',
    resuming: 'Resuming...',
    cancelPause: 'Cancel pause',
    cancelingPause: 'Canceling...',
    report: 'Report',
    markUnpaid: 'Unpaid',
    clearUnpaid: 'Paid',
    updatingUnpaid: 'Updating...',
  },

  // Training Set Stats (generic labels)
  trainingSetStats: {
    cancelled: 'Cancelled',
    completed: 'Completed',
    fired: 'Fired',
    left: 'Left',
    passed: 'Passed',
    planned: 'Planned',
    remaining: 'Remaining',
    skipped: 'Skipped',
    nextTraining: 'Next training',
    totalTrainings: 'Total trainings',
    firedTrainings: 'Fired trainings',
  },

  // Training Templates
  trainingTemplates: {
    title: 'Event set templates',
    subtitle: 'Create reusable templates',
    backToPreferences: 'Back to preferences',
    createNew: 'Create new template',
    edit: 'Edit',
    copy: 'Copy',
    delete: 'Delete',
    confirmDelete: 'Are you sure you want to delete this template?',
    copyConfirm: 'Copy template "{{name}}"?',
    copyPrefix: 'Copy of',
    copyFailed: 'Failed to copy template',
    trainings: 'Trainings',
    maxSkip: 'Max skip',
    noDescription: 'No description',
    empty: {
      title: 'No templates yet',
      description: 'Create your first template to reuse common event patterns',
    },
  },

  // Template Form
  templateForm: {
    createTitle: 'Create template',
    editTitle: 'Edit template',
    subtitle: 'Fill in the properties you want to reuse',
    sections: {
      details: 'Details',
      schedule: 'Schedule',
      goals: 'Goals',
      skipRules: 'Skip rules',
    },
    daysPerWeek: 'days / week',
    tapToAdjustTime: 'Tap to set per-day times',
    noTrainingDays: {
      title: 'No training days selected',
      hint: 'Tap any day above to add it to your schedule',
    },
    durationUnits: {
      hours: 'h',
      minutes: 'min',
    },
    fields: {
      name: 'Template name',
      description: 'Description',
      days: 'Event days',
      defaultStartTime: 'Default start time',
      plannedTrainings: 'Planned trainings',
      duration: 'Event duration',
      maxSkipCount: 'Max skip count',
    },
    placeholders: {
      name: 'E.g., standard week training',
      description: 'Optional description',
      plannedTrainings: 'E.g., 12',
      maxSkipCount: 'E.g., 3',
    },
    hints: {
      defaultStartTime: 'Default time for all event days',
      plannedTrainings: 'Number of events (1-365)',
      duration: 'Max 12 hours total',
      maxSkipCount: 'Maximum missed events allowed (0-365, cannot exceed planned events)',
    },
    actions: {
      create: 'Create template',
      update: 'Update template',
    },
    errors: {
      nameRequired: 'Template name is required',
      nameTooLong: 'Name must be 100 characters or less',
      descriptionTooLong: 'Description must be 255 characters or less',
      noPropertiesSet: 'At least one template property must be set',
      plannedTrainingsRange: 'Planned events must be between 1 and 365',
      durationTooLong: 'Duration cannot exceed 12 hours',
      maxSkipCountRange: 'Max skip count must be between 0 and 365',
      maxSkipExceedsPlanned: 'Max skip count cannot exceed planned events',
    },
  },

  // This Week Trainings
  thisWeekTrainings: {
    weekView: 'Week view',
    dayView: 'Day view',
    sameTime: 'Same time',
    title: 'Events this week',
    subtitle: 'All events scheduled for this week',
    todayTitle: 'Events today',
    todaySubtitle: 'All events scheduled for today',
    emptyTitle: 'No events this week',
    emptyMessage: 'There are no events scheduled for this week',
    emptyTodayTitle: 'No events today',
    emptyTodayMessage: 'There are no events scheduled for today',
    nextTraining: 'Next event',
    asCoach: 'As coach',
    asTrainee: 'As trainee',
  },

  // Pro View
  proView: {
    title: 'Pro View',
    bannerHint: 'See all your trainees side by side',
    emptyMessage: 'No training sessions are scheduled for today',
    noProgram: 'No program assigned',
    noProgramMessage: 'This trainee has no active training program',
  },

  proViewLive: {
    title: 'Pro View',
    subtitle: 'Track and edit live workouts for today’s sessions',
    liveBadge: 'Live',
    startWorkout: 'Start workout',
    start: 'Start',
    starting: 'Starting…',
    moreBlocks: 'Show {{count}} more',
    unplanned: {
      columnTitle: 'Unplanned client',
      columnHint: 'Start a walk-in workout',
      timeLabel: 'Unplanned',
      pickerTitle: 'Choose a client',
      pickerEmpty: 'No clients with an active program assigned by you',
      pickerLoading: 'Loading clients…',
      cancel: 'Cancel',
      alreadyInCarousel: 'Already on the board',
      addFailed: 'Could not add this client',
    },
    error: {
      title: 'Failed to load live view',
      message: 'Could not load live workout data',
      startFailed: 'Failed to start workout for trainee',
    },
  },

  // Coach Dashboard
  coachDashboard: {
    pendingApprovals: 'Pending approvals',
    totalTrainees: 'Total trainees',
    canBeCompleted: 'Can complete',
    activeTrainees: 'Active trainees',
    clientsEventSets: 'Clients event sets',
    preferences: {
      title: 'Settings',
      settingsAndTemplates: 'Settings',
      description: 'Manage your preferences',
    },
  },

  // Trainee Dashboard
  traineeDashboard: {
    subtitle: 'Your events dashboard',
    nextTraining: 'Next event',
    nextSession: {
      title: 'Next session',
      view: 'View',
      noSession: 'No sessions scheduled',
    },
    statsWeek: 'sessions this week',
    statsCoaches: 'coaches',
    explore: 'More features',
    mySets: 'My Sets',
    blocks: 'Blocks',
    programs: 'Programs',
    becomeCoach: {
      title: 'Become a specialist',
      description: 'Become a specialist and start inviting clients',
    },
    closestTrainingSet: {
      label: 'Nearest event set end',
      endsAt: 'Ends at',
      trainingsLeft: 'Events left',
    },
    trainingSetWidget: {
      title: 'Active membership',
      startDate: 'Start date',
      endDate: 'End date',
    },
    manageTrainees: {
      title: 'Manage my trainees',
      description: 'View and manage your trainees',
    },
    viewPrograms: {
      title: 'View my programs',
      description: 'See your training schedule',
    },
    trainingBlocks: {
      title: 'Training Blocks',
      description: 'Workout blocks created via the bot',
    },
    trainingPrograms: {
      title: 'Training Programs',
      description: 'Weekly schedules from your blocks',
    },
    emptyState: {
      waitingCoach: {
        badge: 'Membership',
        title: 'Waiting for your coach',
        subtitle:
          'Your coach is connected. They’ll assign a training set soon — then you’ll see progress and dates here.',
        viewCoaches: 'View coaches',
      },
      getStarted: {
        badge: 'Personal training',
        title: 'Ready to start training?',
        subtitle: 'You have programs ready — activate one to track weekly progress here.',
        hint: 'Browse your programs, pick one, and activate it — this card will show your week and a start button.',
        action: 'Browse programs',
        activeProgress: 'Program progress',
        notStarted: 'Not started',
        noBlocksTitle: 'Create your first block',
        noBlocksSubtitle:
          'Blocks are workout templates. Build one, then turn blocks into a program you can activate.',
        noBlocksAction: 'Create a block',
        noProgramsTitle: 'Build a program',
        noProgramsSubtitle:
          'You have blocks — combine them into a weekly program, then activate it to train.',
        noProgramsAction: 'Create a program',
      },
    },
    activeProgram: {
      title: 'Active program',
      weekOf: 'Week {{current}} of {{total}}',
      view: 'View',
    },
    activeWorkout: {
      subtitle: 'Pick up where you left off',
    },
    activeProgramsSection: 'Active programs',
    completedProgramsSection: 'Completed programs',
    trainingProgram: 'Training program',
    empty: {
      title: 'No training programs yet',
      description: 'Your coach will create training programs for you',
      becomeCoach: 'Become a coach',
    },
  },

  // Trainee Detail
  traineeDetail: {
    title: 'Trainee detail',
    subtitle: 'View and manage trainee information',
    comingSoon: {
      title: 'Coming soon',
      description: 'View and manage trainee details, schedule, and sessions',
    },
  },

  // Become Specialist Flow
  becomeSpecialist: {
    abilities: {
      title: 'What you can do as a specialist',
      subtitle: 'Discover all the powerful features available to you',
      features: [
        'Create and manage event sets for your clients',
        'Track client progress',
        'Set up event set templates',
        'Receive notifications',
        'View detailed statistics and reports',
        'Manage multiple clients efficiently',
      ],
      continue: 'Continue',
    },
    plans: {
      title: 'Available plans',
      subtitle: 'Start with our free plan - you can always upgrade later',
      startWithFree: 'Start with free plan',
      upgradeLater: 'You can upgrade your plan anytime from settings',
      continue: 'Continue',
    },
    invite: {
      title: 'Final step: invite your first client',
      description: 'To complete your transition to a specialist, you need to invite at least one non-virtual client. Once you invite your first client, you\'ll officially become a specialist!',
    },
    leaveCoachFirst: 'You already train with a coach. Leave them first, then you can invite your own clients.',
    goToCoaches: 'Go to my coaches',
  },

  // Bottom Navigation
  navigation: {
    home: 'Home',
    eventSets: 'Event sets',
    clients: 'Clients',
    coaches: 'Coaches',
    contacts: 'Contacts',
    settings: 'Settings',
    schedule: 'Schedule',
    trainees: 'Trainees',
    mySets: 'My Sets',
    more: 'More',
    weekly: 'Schedule',
    sets: 'Sets',
    blocks: 'Blocks',
    programs: 'Programs',
    assistant: 'Assistant',
  },

  // More / Explore screen
  more: {
    title: 'More',
    sections: {
      clients:          { title: 'Clients training' },
      coachTraining:    { title: 'Training with coach' },
      personalTraining: { title: 'Personal training' },
      people:           { title: 'People' },
      account:          { title: 'Account' },
    },
  },

  // Auth
  auth: {
    authenticating: 'Authenticating...',
    pleaseWait: 'Please wait',
    authenticationFailed: 'Authentication failed',
    unableToAuthenticate: 'Unable to authenticate. Please try restarting the app.',
    retry: 'Retry',
  },

  // Subscription
  subscription: {
    price: {
      free: 'Free',
      perMonth: '/month',
    },
    plans: {
      FREE: {
        name: 'Free',
        description: 'Explore the app, coach up to 10 clients if you are a coach, or keep your own training blocks and programs if you train on your own',
        features: [
          'Create your own blocks and programs (if you have no coach)',
          'Up to 10 clients',
          'Assign training to clients',
          'No workout mode unless your coach has Pro',
        ],
      },
      TRIAL: {
        name: 'Trial',
        description: '14 days to try ProView, workout mode, history, and auto-prolongation for free',
        features: [
          'Workout mode',
          'Workout history',
          'ProView live session monitoring',
          'Client workout history',
          'Training set auto-prolongation',
          'Up to 10 clients',
        ],
      },
      SOLO: {
        name: 'Solo',
        description: 'For people who train without a coach — not a coaching plan',
        features: [
          'Workout mode',
          'Workout history',
          'Create your own blocks and programs',
          'Cannot add clients — use Pro to coach',
        ],
      },
      PRO: {
        name: 'Pro',
        description: 'For coaches: full tooling, and clients get workout mode',
        features: [
          'Unlimited clients',
          'Training set auto-prolongation',
          'ProView live session monitoring',
          'Client workout history',
          'Workout mode for you and your clients',
        ],
      },
    },
    status: {
      ACTIVE: 'Active',
      CANCELLED: 'Cancelled',
      PAST_DUE: 'Payment failed',
      TRIALING: 'Trial',
      INCOMPLETE: 'Incomplete',
    },
    mySubscription: 'My subscription',
    subtitle: 'Plan, access, and billing',
    currentPlan: 'Current plan',
    choosePlan: 'Plans',
    access: {
      title: 'Your access',
      fromCoach: 'Follows your coach’s plan',
      workoutMode: 'Workout mode',
      workoutHistory: 'Workout history',
      createOwn: 'Create own blocks & programs',
      addTrainee: 'Add trainees',
      unlimitedClients: 'Unlimited clients',
      proView: 'ProView',
      clientHistory: 'Client workout history',
      autoProlong: 'Auto-prolongation',
    },
    eligibility: {
      coachedAccess: 'You have a coach, so workout mode and history follow their plan. Solo is unavailable until you leave.',
      coachAccess: 'Pro unlocks live monitoring, client history, auto-prolongation, and unlimited trainees. Your clients get workout mode too.',
      soloAccess: 'Solo unlocks workout mode and history for self-training. Pro is only for coaches.',
      soloHiddenWithCoach: 'Solo is for training on your own. Leave your coach first to buy it.',
      soloHiddenAsCoach: 'Coaches use Pro, not Solo. Solo cannot manage clients.',
      proHiddenNotCoach: 'Pro is for coaches. Invite a trainee first, then upgrade.',
      leaveCoachToInvite: 'Leave your coach first to invite clients.',
    },
    clients: 'Clients',
    templates: 'Templates',
    upgrade: 'Upgrade',
    unlockCta: 'Unlock',
    upgradeTo: 'Upgrade to {plan}',
    manageBilling: 'Manage billing',
    cancelSubscription: 'Cancel auto-renewal',
    renewsOn: 'Renews on',
    expiresOn: 'Expires on',
    cancelledOn: 'Cancelled on',
    confirmCancel: 'Cancel auto-renewal? You keep access until the end of the paid period.',
    cancelBeforeSwitch: 'To switch between Solo and Pro, cancel the current plan and wait until it ends.',
    retry: 'Retry',
    loadError: 'Could not load subscription',
    manageDescription: 'Manage your plan and billing',
    checkoutFailed: 'Payment could not be completed. Please try again.',
    paidWithStars: 'Paid securely with Telegram Stars',
    trial: {
      title: 'Try premium free for 14 days',
      description: 'Unlock workout mode, history, ProView, client history, and auto-prolongation for 14 days at no cost.',
      activate: 'Start free trial',
      activateFailed: 'Could not activate the free trial. Please try again.',
      daysLeft: '{days} days left',
      badge: 'Free for 14 days',
      coachedBody: 'While you train with a coach, your features follow their plan. You can activate this trial only after you leave your coach.',
    },
    limitReached: {
      clients: 'Upgrade to PRO for unlimited trainees',
      templates: 'Template limit reached. Upgrade to add more templates.',
      workoutMode: 'Workout mode is available on Solo, or when your coach has Pro.',
      proView: 'ProView is available on the Pro plan.',
      history: 'Workout history is available on Solo, or when your coach has Pro.',
      coachHistory: 'Client workout history is available on the Pro plan.',
    },
    upgradePrompt: {
      title: 'Upgrade your plan',
      description: 'Upgrade to Pro for unlimited trainees and coaching tools.',
      workoutDescription: 'Unlock workout mode to log sets in the gym and keep a history of completed sessions.',
      leaveCoachTitle: 'Leave your coach first',
      leaveCoachToInvite: 'You already train with a coach. Leave them first if you want to invite your own clients.',
      leaveCoachCta: 'Go to my coaches',
      workoutDependsOnCoach: 'Your workout features depend on your coach’s plan. Ask them about Pro, or leave and buy Solo.',
      proViewDescription: 'Upgrade to Pro to monitor live sessions and review client workout history.',
      cta: 'Unlock',
    },
    feature: {
      autoProlongation: {
        upgradeRequired: 'Upgrade to Pro to enable auto-prolongation of training sets.',
      },
    },
    locked: {
      createOwn: 'Joining a coach means you train on their programs. You can’t create your own blocks or programs.',
      soloOnlyTitle: 'Solo users only',
      soloOnlyBody: 'Blocks and programs are available only for solo users without a coach. Your coach assigns training for you.',
    },
  },

  // Training Blocks
  trainingBlocks: {
    title: 'Training Blocks',
    subtitle: 'Your reusable workout blocks',
    loading: 'Loading...',
    createBlock: 'Create block',
    createInBot: 'Import from text',
    createInBotHint: 'Already have a workout saved elsewhere? Copy-paste it or write it as free text — we\'ll try to parse it into a block.',
    editBlock: 'Edit',
    copyBlock: 'Duplicate',
    deleteBlock: 'Delete block',
    noExercises: 'No exercises',
    sets: 'sets',
    reps: 'reps',
    exerciseCount_one: '{{count}} exercise',
    exerciseCount_other: '{{count}} exercises',
    emptyState: {
      title: 'No training blocks yet',
      description: 'Create your first block using the form, or try importing from text.',
    },
    filterAll: 'All',
    filterTitle: 'Filters',
    filterClear: 'Clear',
    filterCollapsedHint: 'Tap to filter by tags',
    filterEmpty: 'No blocks match the selected filters.',
    error: {
      loadFailed: 'Failed to load training blocks',
      copyConfirm: 'Duplicate block "{{name}}"?',
      copyFailed: 'Failed to duplicate block',
      deleteFailed: 'Failed to delete block',
      deleteConfirm: 'Delete block "{{name}}"?',
      deleteConfirmInUse:
        'Delete block "{{name}}"?\n\nIt is used in: {{programs}}.\nIt will also be removed from those programs.',
    },
  },

  // Create Training Block
  createTrainingBlock: {
    title: 'Create Training Block',
    nameLabel: 'Block name',
    namePlaceholder: 'e.g., Upper Body Push',
    tagsLabel: 'Tags',
    tagsAdd: 'Add',
    tagsDone: 'Done',
    tagsEmptyHint: 'No tags yet',
    tagsRemove: 'Remove tag',
    exercisesLabel: 'Exercises',
    addExercise: 'Add exercise',
    addToGroup: 'Add to group',
    groupLabel: 'Exercise group',
    notePlaceholder: 'Note (optional)',
    addNote: 'Add note',
    changeNote: 'Change note',
    doneNote: 'Done',
    ungroup: 'Split into separate exercises',
    removeExercise: 'Remove',
    deleteExercise: 'Remove exercise',
    moveUp: 'Move up',
    moveDown: 'Move down',
    exerciseNamePlaceholder: 'Exercise name',
    setsPlaceholder: 'Sets',
    repsPlaceholder: 'Reps',
    weightPlaceholder: 'Weight (e.g. 80kg)',
    weightLabel: 'Weight',
    submit: 'Create block',
    submitting: 'Creating...',
    orDivider: 'or',
    createInBot: 'Import from text',
    createInBotHint: 'Already have a workout saved elsewhere? Copy-paste it or write it as free text — we\'ll try to parse it into a block.',
    error: {
      nameRequired: 'Block name is required',
      noExercises: 'Add at least one exercise',
      exerciseNameRequired: 'Exercise name is required',
      createFailed: 'Failed to create block',
    },
  },

  aiParse: {
    validation: {
      hint: 'At least {{min}} characters, up to {{max}}',
      textRequired: 'Please enter a description',
      textTooShort: 'Enter at least {{min}} characters to parse',
      textTooLong: 'Text must be at most {{max}} characters',
      correctionRequired: 'Please describe what to fix',
      correctionTooLong: 'Correction must be at most {{max}} characters',
      inputTooLarge: 'Input is too large to parse',
    },
  },

  // Create Training Block From Text
  createTrainingBlockFromText: {
    title: 'Import block from text',
    subtitle: 'Already have a workout saved elsewhere? Copy-paste it or write it as free text.',
    textareaLabel: 'Workout description',
    textareaPlaceholder: 'e.g., 3 sets bench press 80kg, 4x10 squats 100kg, pull-ups to failure...',
    parseButton: 'Parse it',
    parsing: 'Parsing...',
    previewTitle: 'Preview',
    previewSubtitle: 'Review the parsed workout block',
    confirmButton: 'Create Block',
    confirming: 'Creating...',
    startOver: 'Start over',
    correctionsLabel: 'Need adjustments?',
    correctionPlaceholder: 'Describe what to fix, e.g. "squats should be 5 sets, not 4"...',
    reparseButton: 'Refine',
    reparsing: 'Refining...',
    attemptsLeft: '{{count}} refinement(s) left',
    error: {
      parseFailed: 'Failed to parse workout description',
      createFailed: 'Failed to create block',
      quotaExceeded: 'AI usage limit reached. Please try again later.',
    },
  },

  // Create Training Program From Text
  createTrainingProgramFromText: {
    title: 'Import program from text',
    subtitle: 'Already have a program saved elsewhere? Copy-paste it or write it as free text.',
    textareaLabel: 'Program description',
    textareaPlaceholder: 'e.g., Day 1: chest and triceps — bench press 4x8 80kg, dips 3x12...',
    parseButton: 'Parse it',
    parsing: 'Parsing...',
    programNameLabel: 'Program name',
    programNamePlaceholder: 'e.g., Push Pull Legs',
    blockNameLabel: 'Block name',
    confirmButton: 'Create Program',
    confirming: 'Creating...',
    startOver: 'Start over',
    correctionsLabel: 'Need adjustments?',
    correctionPlaceholder: 'Describe what to fix, e.g. "day 2 should have more leg exercises"...',
    reparseButton: 'Refine',
    reparsing: 'Refining...',
    attemptsLeft: '{{count}} refinement(s) left',
    error: {
      parseFailed: 'Failed to parse program description',
      createFailed: 'Failed to create program',
      quotaExceeded: 'AI usage limit reached. Please try again later.',
    },
  },

  // Training Tags
  trainingTagSections: {
    muscle: 'Muscle groups',
    level: 'Level',
    gender: 'Gender',
    type: 'Type',
  },
  trainingTags: {
    legs: 'Legs',
    arms: 'Arms',
    shoulders: 'Shoulders',
    chest: 'Chest',
    back: 'Back',
    core: 'Core',
    cardio: 'Cardio',
    full_body: 'Full body',
    glutes: 'Glutes',
    calves: 'Calves',
    novice: 'Novice',
    medium: 'Medium',
    hard: 'Hard',
    advanced: 'Advanced',
    female: 'Female',
    male: 'Male',
    unisex: 'Unisex',
    hit: 'HIT',
    standard: 'Standard',
  },

  // Edit Training Block
  editTrainingBlock: {
    title: 'Edit Training Block',
    viewTitle: 'Training Block',
    submit: 'Save changes',
    submitting: 'Saving...',
    error: {
      loadFailed: 'Failed to load training block',
      saveFailed: 'Failed to save changes',
    },
  },

  // Training Programs
  trainingPrograms: {
    title: 'Training Programs',
    subtitle: 'Ordered sequences of training blocks',
    loading: 'Loading...',
    createProgram: 'Create program',
    createFromText: 'Import from text',
    createFromTextHint: 'Already have a program saved elsewhere? Copy-paste it or write it as free text — we\'ll try to parse it into a program.',
    editProgram: 'Edit',
    deleteProgram: 'Delete',
    copyProgram: 'Copy',
    copyConfirm: 'Copy program "{{name}}"?',
    copyPrefix: 'Copy of',
    copyFailed: 'Failed to copy program',
    activeBadge: 'Active',
    activationHistory: 'History',
    blockCount_one: '{{count}} block',
    blockCount_other: '{{count}} blocks',
    blockIndex: 'Block {{n}}',
    emptyState: {
      title: 'No programs yet',
      description: 'A program is an ordered sequence of training blocks applied to your weekly schedule.',
    },
    filterEmpty: 'No programs match the selected filters.',
    error: {
      loadFailed: 'Failed to load training programs',
      deleteFailed: 'Failed to delete program',
      deleteConfirm: 'Delete program "{{name}}"?',
    },
    activate: {
      title: 'Activate Program',
      forMyselfButton: 'Activate for myself',
      durationLabel: 'Duration',
      durationWeeks: '{{count}} week(s)',
      startButton: 'Start Program',
      starting: 'Starting...',
      noScheduleWarning: 'No training schedule set. Add your weekly schedule to continue.',
      goToSchedule: 'Set up schedule',
      successMessage: 'Program activated!',
      error: {
        activateFailed: 'Failed to activate program',
        deactivateFailed: 'Failed to deactivate program',
      },
    },
    deactivate: 'Deactivate',
    assignProgram: {
      title: 'Assign Program',
      assignButton: 'Assign to trainee',
      assigning: 'Assigning...',
      deassignButton: 'Remove assignment',
      selectProgram: 'Select a program',
      searchPlaceholder: 'Search programs…',
      noPrograms: 'No programs available',
      noSearchResults: 'No programs match your search',
      selectTrainee: 'Select trainee',
      noTrainees: 'No trainees available',
      assignedTrainees: 'Assigned trainees',
      fromCoachBadge: 'From coach',
      currentAssignment: 'Current assignment',
      scheduleLabel: 'Schedule',
      startDate: 'Start date',
      endDate: 'End date',
      quickDuration: 'Duration',
      durationWeeks: '{{count}} week(s)',
      weekChip: '{{count}}w',
      endBeforeStart: 'End date must be on or after start date',
      error: {
        assignFailed: 'Failed to assign program',
        deassignFailed: 'Failed to remove assignment',
      },
      onDemand: {
        cta: 'Create on demand',
        ctaHint: 'Build a one-off program for this trainee',
        title: 'On-demand program',
        daysLabel: 'Blocks',
        pickBlock: 'Pick saved block',
        createBlock: 'Create new block',
        addBlock: 'Add block',
        removeDay: 'Remove',
        fromLibrary: 'From library',
        newBlockBadge: 'New (not saved to library)',
        noSavedBlocks: 'No saved blocks yet',
        defaultBlockName: 'Block {{n}}',
        error: {
          daysRequired: 'Add at least one block',
          exerciseRequired: 'Add at least one exercise with a name',
        },
      },
    },
    viewProjections: 'View schedule',
    projections: {
      title: 'Program Schedule',
      sessions: '{{count}} sessions',
      weeks: '{{count}} weeks',
      empty: 'No sessions scheduled',
      cancelledBadge: 'Cancelled',
    },
    history: {
      title: 'Activation History',
      empty: 'No activation history yet',
      endedLabel: 'Ended',
      ongoingLabel: 'Ongoing',
      startedLabel: 'Started',
      saveToLibrary: 'Add to saved',
      saving: 'Adding...',
      saved: 'Saved',
      unsavedRetentionHint: 'Unsaved programs older than 3 months are removed from history. Add them to saved to keep them.',
    },
    saveToLibrary: 'Save to library',
    saveBlockToLibrary: 'Save block',
    savingToLibrary: 'Saving...',
  },

  // Create / Edit Training Program
  createTrainingProgram: {
    title: 'Create Program',
    editTitle: 'Edit Program',
    nameLabel: 'Program name',
    namePlaceholder: 'e.g., Push Pull Legs',
    blocksLabel: 'Block sequence',
    saving: 'Saving...',
    submit: 'Create program',
    submitEdit: 'Save changes',
    noBlocksTitle: 'No training blocks available',
    noBlocksDescription: 'Create blocks via',
    noBlocksDescriptionSuffix: 'in the bot first.',
    addBlock: 'Add block',
    selectBlock: 'Select block',
    changeBlock: 'Change',
    addNBlocks: 'Add {{count}} block(s)',
    currentBlock: 'Currently selected',
    loading: 'Loading...',
    pickSavedBlock: 'Pick saved block',
    createNewBlock: 'Create new block',
    newBlockBadge: 'New',
    noSavedBlocks: 'No saved blocks yet',
    defaultBlockName: 'Block {{n}}',
    createFromText: 'Import from text',
    createFromTextHint: 'Already have a program saved elsewhere? Copy-paste it or write it as free text — we\'ll try to parse it into a program.',
    error: {
      nameRequired: 'Program name is required',
      noBlocksSelected: 'Add at least one block',
      exerciseRequired: 'Add at least one exercise with a name',
      loadBlocksFailed: 'Failed to load training blocks',
      createFailed: 'Failed to create program',
      updateFailed: 'Failed to update program',
      loadFailed: 'Failed to load program',
    },
  },

  // Preferences
  preferences: {
    title: 'Preferences',
    subtitle: 'Manage your settings',
    backToDashboard: 'Back to dashboard',
    training: {
      templates: {
        title: 'Event templates',
        description: 'Create and manage reusable event templates',
      },
    },
    userProfile: {
      title: 'My profile',
      description: 'View your name, photo and username',
      firstName: 'First name',
      lastName: 'Last name',
      username: 'Username',
      noInfo: 'No profile info',
      photoSyncEnabled: 'Photo synchronization',
      photoSyncDescription: 'Sync your Telegram profile photo to the app. Sync runs during the day, so it may take some time to update here after you change your photo in Telegram.',
      syncPhotoNow: 'Sync photo now',
      syncingPhoto: 'Syncing...',
      syncPhotoSuccess: 'Photo synchronized successfully!',
      syncPhotoError: 'Failed to sync photo. Please try again.',
      syncPhotoDisabled: 'Please enable photo synchronization first.',
      syncPhotoFailedWithPrivacy: 'Failed to sync photo. Please check your Telegram privacy settings and make sure your profile photo is visible to everyone. Go to Telegram Settings > Privacy and Security > Profile Photo and set it to "Everyone".',
    },
    notifications: {
      title: 'Notifications',
      description: 'Choose which notifications you want to receive',
      traineeSection: 'As trainee',
      coachSection: 'As coach',
      myLastTrainingLeft: 'My last event left',
      traineesLastTrainingLeft: "Clients' last event left",
      trainingsCanBeCompleted: 'Events that can be completed',
    },
    trainingSchedule: {
      title: 'Training schedule',
      description: 'Set your weekly training days and times',
      scheduleLabel: 'Training days',
      daysPerWeek: 'days / week',
      tapToAdjustTime: 'Tap a time to adjust',
      emptyTitle: 'No training days selected',
      emptyHint: 'Tap any day above to add it to your weekly schedule',
      save: 'Save schedule',
      saving: 'Saving...',
      saveSuccess: 'Schedule saved',
      error: {
        loadFailed: 'Failed to load schedule',
        saveFailed: 'Failed to save schedule',
      },
    },
    support: {
      title: 'Report a problem',
      description: 'Send a bug report or blocking issue to support',
    },
    saveSuccess: 'Preferences saved successfully',
    saveError: 'Failed to save preferences',
  },

  support: {
    title: 'Report a problem',
    subtitle: 'Tell us what went wrong. We review every report as soon as we can.',
    categoryLabel: 'What kind of problem?',
    categories: {
      bug: {
        label: 'Bug',
        description: 'Something looks wrong or behaves unexpectedly',
      },
      blocking: {
        label: 'Blocking problem',
        description: 'I cannot continue using a core feature',
      },
    },
    messageLabel: 'Describe the problem',
    messagePlaceholder: 'What happened? What did you expect?',
    submit: 'Send report',
    submitting: 'Sending…',
    success: 'Report sent. Thank you!',
    successHint: 'We received your report and will review it as soon as possible.',
    usernameMissing: {
      title: 'Telegram username required',
      message: 'You do not have a Telegram username set, so support cannot contact you if a follow-up is needed.',
      details: 'Open Telegram Settings → Username, set a username, then reopen the app and try again.',
    },
    errors: {
      validation: 'Please check your message and try again.',
      rateLimited: 'Please wait a few minutes before sending another report.',
      submitFailed: 'Failed to send the report. Please try again.',
    },
  },

  workout: {
    startTitle: 'Start training',
    startSubtitle: 'Pick a block and follow your workout with history.',
    viewProgram: 'View program',
    freePlan: {
      title: 'Your program',
      subtitle: 'See your current program and train with it in the gym.',
      bannerTitle: 'Free plan',
      bannerBody:
        'On the free plan you can see your current program and train with it. Upgrade to Solo to track progress during the workout and keep history.',
      upgradeCta: 'Unlock',
      noProgram: 'Activate a program to see it here and train with it.',
      goToPrograms: 'Go to programs',
    },
    startBlock: 'Start',
    loading: 'Loading…',
    activeProgram: 'Active program',
    assignedByCoach: 'Assigned by your coach',
    programBlocks: 'Program blocks',
    chooseSavedBlock: 'Choose a saved block',
    exerciseCount: '{{count}} exercises',
    noExercises: 'No exercises',
    noBlocksInProgram: 'This program has no blocks yet.',
    noSavedBlocks: 'You have no saved blocks yet.',
    goToBlocks: 'Go to blocks',
    unlockCta: 'Unlock',
    resumeTitle: 'Workout in progress',
    resumeSubtitle: 'Continue {{block}}',
    resumeCta: 'Resume workout',
    sessionTitle: 'Workout',
    progress: '{{current}} / {{total}}',
    progressGroups: '{{current}} / {{total}} groups',
    previousExercise: 'Previous',
    nextExercise: 'Next up',
    navHint: 'Use the arrows on the card or the previews to switch exercises.',
    navHintGroups: 'Use the arrows to move between exercise groups.',
    plannedSets: 'Sets',
    plannedReps: 'Reps',
    plannedWeight: 'Weight',
    targetLabel: 'Target',
    setsLabel: 'Sets',
    setWeight: 'Weight',
    setReps: 'Reps',
    setWeightLabel: 'Set {n} weight',
    setRepsLabel: 'Set {n} reps',
    addSet: 'Add set',
    removeSet: 'Remove',
    commentLabel: 'Comment',
    commentToggleHint: 'Optional note about this exercise',
    commentPlaceholder: 'How it felt, cues, notes…',
    blockNoteLabel: 'Block note',
    addWorkoutNote: 'Add workout note',
    changeWorkoutNote: 'Change note',
    doneNote: 'Done',
    logOptionalHint: 'Logging is optional — use Next when you are ready.',
    historyTitle: 'Previous times',
    historyEmpty: 'No previous logs for this exercise yet.',
    historyNoActuals: 'Completed (no numbers logged)',
    historySets: 'Sets',
    historyComment: 'Note',
    historySetsHint: 'Weight × reps for each logged set.',
    historyShowMore: 'Show {{count}} more',
    historyShowLess: 'Show less',
    repsSuffix: 'reps',
    prev: 'Back',
    next: 'Next',
    finish: 'Finish workout',
    finishing: 'Finishing…',
    cancel: 'Cancel workout',
    canceling: 'Canceling…',
    cancelConfirm: 'Cancel this workout? All logged progress will be deleted and will not appear in history.',
    backToStart: 'Back to start',
    error: {
      loadFailed: 'Failed to load training mode',
      startFailed: 'Failed to start workout',
      loadSessionFailed: 'Failed to load workout session',
      saveFailed: 'Failed to save log',
      finishFailed: 'Failed to finish workout',
      cancelFailed: 'Failed to cancel workout',
    },
    conflict: {
      log: 'This exercise was updated by someone else. Showing the latest values — edit again to save yours.',
      session: 'The workout moved while you were switching exercises. Showing the latest state.',
    },
  },

  quickAccess: {
    title: 'Quick Access',
    subtitle: 'Your shortcuts on the home screen',
    editHint: 'Edit',
    saveButton: 'Save',
    selectHint: 'Select up to 3 sections for quick access',
    saveSuccess: 'Shortcuts saved',
    saveError: 'Failed to save shortcuts',
    items: {
      'this-week-trainings': 'This Week',
      'all-training-sets': 'Training Sets',
      'active-training-set': 'Active Training set',
      'training-blocks': 'Blocks',
      'training-programs': 'Programs',
      'training-schedule': 'Schedule',
      'start-training': 'Start training',
      'program-history': 'History',
      'pro-view': 'Pro View',
      'trainees': 'Clients',
      'templates': 'Templates',
      'invite': 'Invite',
      'my-coaches': 'My Coaches',
      'become-specialist': 'Be a Coach',
      'my-profile': 'My Profile',
      'subscription': 'Subscription',
      'support': 'Report a problem',
      'notifications-settings': 'Notifications',
      'quick-access': 'Quick Access',
    },
  },

  historyPage: {
    title: 'History',
    clientTitle: 'History · {{name}}',
    tabs: {
      sets: 'Sets',
      programs: 'Programs',
      workouts: 'Workouts',
    },
    workouts: {
      empty: 'No completed workouts yet',
      entitlementRequired: 'Unlock Training mode to keep workout history.',
      unlockTitle: 'Keep workout history',
      unlockBody:
        'On the free plan, completed workouts are not saved with progress. Unlock Solo to log sessions and track how you improve.',
      unlockCta: 'Unlock',
      coachPath:
        'If you train with a coach, they should send you an invite. A coach on Pro includes workout history for you.',
      unnamedBlock: 'Workout',
      loadingDetail: 'Loading details…',
      planned: 'Planned',
      deleteAria: 'Delete workout',
      deleteConfirm: 'Delete workout "{{name}}"? This cannot be undone.',
      deleteFailed: 'Failed to delete workout',
    },
    error: {
      loadFailed: 'Failed to load history',
      clientNotFound: 'Client not found',
    },
  },

  tour: {
    coach: {
      skip: 'Skip',
      next: 'Next',
      done: 'Done',
      welcome: {
        title: 'Coach tour',
        startTour: 'Show me around',
        slides: [
          {
            title: 'Welcome, coach',
            body: 'A quick look at the places you’ll use every day with clients.',
          },
          {
            title: 'Plan and assign',
            body: 'Build sets, blocks, and programs — then assign them to clients.',
          },
          {
            title: 'Stay on schedule',
            body: 'Check the week, run live sessions, and manage clients from More.',
          },
        ],
      },
      steps: {
        todayTitle: 'Today’s sessions',
        todayBody: 'Your day at a glance — tap to open today’s schedule.',
        clientsTitle: 'Your clients',
        clientsBody: 'Invite clients or open someone quickly from this strip.',
        moreTitle: 'Clients & coaching',
        moreBody: 'Clients, templates, Pro View, and the rest of your coaching tools live here.',
        assistantTitle: 'Need help?',
        assistantBody: 'If you still have some questions, try our assistant.',
      },
    },
    traineeSolo: {
      skip: 'Skip',
      next: 'Next',
      done: 'Done',
      welcome: {
        title: 'Trainee tour',
        startTour: 'Show me around',
        slides: [
          {
            title: 'Welcome',
            body: 'Train on your own — create a training program to start working out.',
          },
          {
            title: 'Your tools',
            body: 'Home, Quick Access, and More keep planning and workouts within reach.',
          },
        ],
      },
      steps: {
        homeTitle: 'Your training home',
        homeBody: 'Create a training program to start a workout. Progress will show on this card.',
        quickAccessTitle: 'Quick Access',
        quickAccessBody: 'Pin the tools you use most so Home stays one tap from training.',
        personalTitle: 'Personal training',
        personalBody: 'Blocks, programs, start workout, and history live here.',
        createBlockTitle: 'Create a block',
        createBlockBody: 'Start here — each block is a reusable workout session.',
        createProgramTitle: 'Create a program',
        createProgramBody: 'Combine blocks into a program, then activate it to train.',
        accountTitle: 'Account',
        accountBody: 'Profile, subscription, notifications, and quick-access settings.',
        becomeCoachTitle: 'Want to coach?',
        becomeCoachBody: 'Tap Be a Coach when you’re ready to invite clients and manage trainings.',
        assistantTitle: 'Need help?',
        assistantBody: 'If you still have questions, try our assistant.',
      },
    },
    traineeWithCoach: {
      skip: 'Skip',
      next: 'Next',
      done: 'Done',
      welcome: {
        title: 'Training with a coach',
        startTour: 'Show me around',
        slides: [
          {
            title: 'You have a coach',
            body: 'Your coach can assign sets and schedule sessions — here’s what changed.',
          },
          {
            title: 'Stay in sync',
            body: 'Your coach and set are on Home. Start a workout and history live under Training with coach in More.',
          },
        ],
      },
      steps: {
        setTitle: 'Your membership',
        setBody: 'Your membership lives here — coach, progress, and sessions show once a set is assigned.',
        withCoachTitle: 'Training with coach',
        withCoachBody: 'Active training set, this week’s sessions, start workout, and history — all here.',
        assistantTitle: 'Need help?',
        assistantBody: 'Still have questions? Try the assistant.',
      },
    },
    trainingSetDetailCoach: {
      skip: 'Skip',
      next: 'Next',
      done: 'Done',
      steps: {
        overviewTitle: 'Training set',
        overviewBody: 'This is the membership — progress, upcoming sessions, and manage actions live here.',
        progressTitle: 'Progress',
        progressBody: 'Completed, fired, and remaining sessions. The marker shows where they are now.',
        completeTitle: 'Complete',
        completeBody: 'Finish the set when remaining sessions are done. You can start the next one automatically.',
        reportTitle: 'Report',
        reportBody: 'Send a summary of this set to Telegram.',
        unpaidTitle: 'Payment',
        unpaidBody: 'Mark the set unpaid or paid to track balances.',
        pauseTitle: 'Pause',
        pauseBody: 'Freeze the schedule for a date range — sessions continue after the pause.',
        resumeTitle: 'Resume',
        resumeBody: 'End the pause early and continue the schedule.',
        cancelPauseTitle: 'Cancel pause',
        cancelPauseBody: 'Remove a planned pause if you no longer need it.',
        deleteTitle: 'Delete',
        deleteBody: 'Remove this set and its sessions. This cannot be undone.',
      },
    },
    trainingSetDetailTrainee: {
      skip: 'Skip',
      next: 'Next',
      done: 'Done',
      steps: {
        overviewTitle: 'Training set',
        overviewBody: 'This is your membership — progress and upcoming sessions live here. Your coach manages the set.',
        progressTitle: 'Progress',
        progressBody: 'Completed, fired, and remaining sessions. The marker shows where you are now.',
      },
    },
  },
};

export type TranslationKeys = typeof en;
